import { useCallback, useMemo } from "react";
import type { FormEvent } from "react";
import { Alert, Button, Checkbox, List, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { LegacyReportComparisonFinding } from "../../../types/legacyReportReview";

interface Props {
  blockers: string[];
  findings: LegacyReportComparisonFinding[];
  pending: boolean;
  onApprove: (minorIds: string[]) => Promise<void>;
}

export default function LegacyReportApproval({
  blockers,
  findings,
  pending,
  onApprove,
}: Props) {
  const minor = useMemo(
    () => findings.filter((finding) => finding.severity === "minor"),
    [findings]
  );
  const form = useForm<{
    sourceReviewed: boolean;
    acknowledgedMinorIds: string[];
  }>({
    initialValues: {
      sourceReviewed: false,
      acknowledgedMinorIds: [] as string[],
    },
    validateInputOnBlur: true,
    validate: {
      sourceReviewed: (value: boolean) =>
        value
          ? null
          : "Confirm that you reviewed the complete original and reconstructed report.",
      acknowledgedMinorIds: (value: string[]) =>
        minor.every((finding) => value.includes(finding.id))
          ? null
          : "Review and acknowledge each remaining minor discrepancy.",
    },
  });
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const result = form.validate();
      if (result.hasErrors) {
        const node = form.getInputNode(Object.keys(result.errors)[0]);
        node?.scrollIntoView?.({ block: "center" });
        node?.focus();
        return;
      }
      if (!blockers.length && !pending)
        void onApprove(form.getValues().acknowledgedMinorIds);
    },
    [form, blockers, pending, onApprove]
  );
  return (
    <form onSubmit={handleSubmit} noValidate>
      <Stack gap="md">
        {blockers.length > 0 && (
          <Alert color="orange" title="Before approval">
            <List size="sm">
              {blockers.map((blocker) => (
                <List.Item key={blocker}>{blocker}</List.Item>
              ))}
            </List>
          </Alert>
        )}
        {minor.length > 0 && (
          <Checkbox.Group
            label="Remaining minor discrepancies"
            required
            {...form.getInputProps("acknowledgedMinorIds")}
          >
            <Stack gap="xs" mt="xs">
              {minor.map((finding) => (
                <Checkbox
                  key={finding.id}
                  value={finding.id}
                  label={finding.description}
                />
              ))}
            </Stack>
          </Checkbox.Group>
        )}
        <Checkbox
          required
          label="I reviewed every page, literal wording, historical attribution, and preserved graphic against the original"
          {...form.getInputProps("sourceReviewed", { type: "checkbox" })}
        />
        <Text size="sm" c="dimmed">
          Approval freezes this historical baseline. New clinical changes are
          made afterward through the report revision workflow with current
          attribution.
        </Text>
        <Button type="submit" loading={pending} fullWidth>
          Approve historical reconstruction
        </Button>
      </Stack>
    </form>
  );
}
